import { useState, useEffect, useCallback, useRef } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';

export interface ShareModalProps {
  entityName: string;
  entitySubtitle?: string;
  shareUrl?: string;
  isOpen: boolean;
  onClose: () => void;
}

const shareOptions = [
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'ri-facebook-fill',
    color: 'bg-[#1877F2] hover:bg-[#166fe5]',
    action: (url: string) => {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    },
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'ri-whatsapp-line',
    color: 'bg-[#25D366] hover:bg-[#22c35e]',
    action: (url: string) => {
      window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank');
    },
  },
  {
    id: 'x',
    label: 'X / Twitter',
    icon: 'ri-twitter-x-line',
    color: 'bg-[#0F1419] hover:bg-[#1a1f24]',
    action: (url: string) => {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    },
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: 'ri-telegram-line',
    color: 'bg-[#26A5E4] hover:bg-[#2298d4]',
    action: (url: string) => {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    },
  },
  {
    id: 'copy',
    label: 'Copiar enlace',
    icon: 'ri-link',
    color: 'bg-background-100 hover:bg-background-200/70 text-foreground-700 border border-background-300/60',
    action: async (url: string) => {
      try {
        await navigator.clipboard.writeText(url);
        SwalAlert.success('¡Enlace copiado al portapapeles!');
      } catch {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        SwalAlert.success('¡Enlace copiado al portapapeles!');
      }
    },
  },
  {
    id: 'qr',
    label: 'Código QR',
    icon: 'ri-qr-code-line',
    color: 'bg-background-100 hover:bg-background-200/70 text-foreground-700 border border-background-300/60',
    action: () => {
      SwalAlert.info('Próximamente podrás descargar el QR.');
    },
  },
];

export default function ShareModal({ entityName, entitySubtitle, shareUrl, isOpen, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handler);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      SwalAlert.success('¡Enlace copiado al portapapeles!');
    } catch {
      const tempInput = document.createElement('input');
      tempInput.value = url;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div ref={overlayRef} onClick={handleOverlayClick} className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-md mx-4 p-6 z-10 shadow-2xl animate-[fadeIn_0.2s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-foreground-950">Compartir</h3>
            <p className="text-sm text-foreground-500 mt-0.5">
              {entityName}{entitySubtitle ? ` · ${entitySubtitle}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer text-foreground-500 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Share Options Grid */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {shareOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => option.action(url)}
              className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm font-medium ${
                option.color
              } ${option.id === 'copy' || option.id === 'qr' ? '' : 'text-white'}`}
            >
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                <i className={`${option.icon} text-xl`}></i>
              </div>
              <span className="text-xs">{option.label}</span>
            </button>
          ))}
        </div>

        {/* Direct Copy Link */}
        <div className="flex items-center gap-2 bg-background-100 rounded-xl px-4 py-3 border border-background-200/70">
          <i className="ri-link text-foreground-400 text-base shrink-0"></i>
          <input
            type="text"
            readOnly
            value={url}
            className="flex-1 bg-transparent text-sm text-foreground-700 truncate outline-none select-all"
          />
          <button
            onClick={handleCopy}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              copied
                ? 'bg-emerald-500 text-white'
                : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
            }`}
          >
            <span className="flex items-center gap-1">
              <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
              {copied ? 'Copiado' : 'Copiar'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}